import { EmployeeModelType } from '@teinor/erp/types/company/userInner/employee'
import { ConnectedProps, connect } from 'react-redux'
import { useLocation } from 'react-router'
import { ComponentWithPermissions } from '../../../../../../baseComponents/CreateRoutering/types'
import { permissionCheck } from '../../../../../../baseComponents/PermissionChecker/function'
import { TwinTableLink } from '../../../../../../baseComponents/TwinTable'
import BaseScreenTable from '../../../../../../baseComponents/TwinTable/BaseScreenTable'
import { TableComponentProps } from '../../../../../../baseComponents/TwinTable/BaseScreenTable/types'
import { CustomRenderBoolean, CustomRenderStateName, CustomRenderString,DeleteRow, EditRow } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import withLoading from '../../../../../../utils/hoc/withLoading'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../utils/reducers'
import { WorkingCenterPayload } from '../../../../../../utils/reducers/company/workingCenters'
import { GroupPayload } from '../../../../../../utils/reducers/groups/groups'
import ModalCreateEmployee from './ModalCreateEmployee'
import { getBasePath } from '../../../../../../utils/globals/link'
import { faRotateLeft } from '@fortawesome/pro-light-svg-icons'
import ModalDeleteEmployee from './ModalDeleteEmployee'

interface EmployeesProps extends ComponentWithPermissions { }

const Employees: React.FC<EmployeesProps> = ({ userPermissions }) => {
    const { t } = useTwinTranslation()

    const pageData = {
        title: t('employees', 'Empleados'),
        newText: t('newEmployee', 'Nuevo empleado'),
    }

    return (
        <BaseScreenTable TableComponent={TableEmployeeConnect} CreateModalComponent={ModalCreateEmployee} DeleteModalComponent={ModalDeleteEmployee} pageData={pageData} userPermissions={userPermissions} />
    )
}

type TableEmployeesProps = TableComponentProps & ReduxWorkingCenters & { }

const TableEmployees: React.FC<TableEmployeesProps> = ({ userPermissions, setTableInstance, setOpened, workingCenters }) => {
    const { t } = useTwinTranslation()
    const location = useLocation().pathname
    const columns: ColumnTableSchema<EmployeeModelType> = [
        {
            id: 'name',
            dataKey: 'fullname_short',
            label: t('name', 'Nombre'),
            sortable: true,
            minWidth: 130,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            }
        },
        {
            id: 'address',
            dataKey: 'address',
            label: t('address', 'Dirección'),
            sortable: true,
            minWidth: 140,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('address', 'Dirección'), }
            }
        },
        {
            id: 'additional_address',
            dataKey: 'additional_address',
            label: t('additional_address_2', 'Dirección 2'),
            sortable: true,
            minWidth: 150,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('additional_address', 'Dirección adicional'), }
            }
        },
        {
            id: 'city',
            dataKey: 'city',
            label: t('city', 'Ciudad'),
            sortable: true,
            minWidth: 120,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('city', 'Ciudad'), }
            }
        },
        {
            id: 'state',
            dataKey: 'state',
            label: t('state', 'Provincia'),
            customRender: (parameterValue, allRowData) => <CustomRenderStateName stateId={parameterValue} allRowData={allRowData} />,
        },
        {
            id: 'defaultWorkingCenterId',
            dataKey: 'defaultWorkingCenterId',
            label: t('workingCenter', 'Centro de trabajo'),
            minWidth: 130,
            customRender: (parameterValue) => <CustomRenderString value={workingCenters?.[parameterValue as any]?.name || ''} />
        },
        {
            id: 'clientManager',
            dataKey: 'clientManager',
            label: t('clientManager', 'Gestor de clientes'),
            width: 150,
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={115} />
        },
        {
            id: 'active',
            dataKey: 'active',
            label: t('active', 'Activo'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={45} />,
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: {
                    items: {
                        'true': { name: t('actived', 'Activados') },
                        'false': { name: t('disabled', 'Desactivados') },
                        'all': { name: t('all', 'Todos') }
                    }, label: t('active', 'Activo')
                }
            },
            searchKey: 'active',
            generalSearchable: false
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />,
            havePermission: permissionCheck(userPermissions, 'update')
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('delete', 'Eliminar'),
            customRender: (parameterValue, allRowData) => {
                const icon = !allRowData?.active ? faRotateLeft : undefined
                const messageTooltip = !allRowData?.active ? t('activate', 'Activar') : t('desactive', 'Desactivar')
                return <DeleteRow key={'delete' + parameterValue} {...{ icon, parameterValue, allRowData, messageTooltip }} onClick={(_idk, allRowData) => setOpened({ type: 'delete', allRowData })} />
            },
            havePermission: permissionCheck(userPermissions, 'delete')
        },
    ]
    return (
        <TwinTableLink createRowDataLink={[getBasePath(location, 'employees'), ['$id', 'main']]} name='employees' columns={columns} getDataFrom='/api/app/employee/getAllEmployees' onRowClick={(_id, rowData) => setOpened({
            type: 'edit',
            allRowData: rowData
        })} setTableInstance={setTableInstance} />
    )
}

const workingCentersDispatch = {
    setWorkingCenters: (payload: WorkingCenterPayload) => ({ type: 'CHANGE_WORKINGCENTER', payload }),
    setGroups: (payload: GroupPayload) => ({ type: 'CHANGE_GROUP', payload }),
}

export type ReduxWorkingCenters = ConnectedProps<typeof workingCentersConnect>
const mapWorkingCentersConnector = (state: AllReduxPayloads) => ({ workingCenters: state.workingCenters, groups: state.groups })
const workingCentersConnect = connect(mapWorkingCentersConnector, workingCentersDispatch)

const workingCentersConnectLoading = withLoading(TableEmployees, [{ fetchUrl: '/api/app/workingCenter/getAllWorkingCentersComplete', propName: 'workingCenters', setFunctionName: 'setWorkingCenters' }, { fetchUrl: '/api/app/group/getAllGroupsListing', propName: 'groups', setFunctionName: 'setGroups' }])

const TableEmployeeConnect = workingCentersConnect(workingCentersConnectLoading)


export default Employees